import React, { useMemo, useState } from "react";
import { Users, Check, ChevronDown, ChevronUp, Search } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { useSession } from "../../SessionContext";

const MAX_COUNCIL = 5;
const GROUP_ORDER = ["generalist", "medical", "surgical", "diagnostic"];

/**
 * Per-query council selector. Empty selection means the server picks doctors
 * for the case. Expanding shows personas grouped by specialty group.
 */
const CouncilSelector = ({ personas = [], loading = false, disabled = false }) => {
  const { t } = useLanguage();
  const { selectedCouncil, setSelectedCouncil } = useSession();
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");

  const selected = selectedCouncil ?? [];
  const groupLabel = {
    generalist: t("query.councilGroupGeneralist"),
    medical: t("query.councilGroupMedical"),
    surgical: t("query.councilGroupSurgical"),
    diagnostic: t("query.councilGroupDiagnostic"),
  };

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return personas;
    return personas.filter((p) => {
      const name = (p.display_name || "").toLowerCase();
      const specialty = (p.specialty || "").toLowerCase();
      return name.includes(needle) || specialty.includes(needle);
    });
  }, [personas, search]);

  const grouped = useMemo(() => {
    const map = new Map(GROUP_ORDER.map((group) => [group, []]));
    filtered.forEach((persona) => {
      const group = GROUP_ORDER.includes(persona.group) ? persona.group : "medical";
      map.get(group).push(persona);
    });
    return GROUP_ORDER
      .map((group) => ({ group, items: map.get(group) }))
      .filter((bucket) => bucket.items.length > 0);
  }, [filtered]);

  if (loading || personas.length === 0) return null;

  const toggle = (id) => {
    if (disabled) return;
    if (selected.includes(id)) {
      setSelectedCouncil(selected.filter((x) => x !== id));
      return;
    }
    if (selected.length >= MAX_COUNCIL) return;
    setSelectedCouncil([...selected, id]);
  };

  const renderChip = (p) => {
    const isOn = selected.includes(p.id);
    const atLimit = !isOn && selected.length >= MAX_COUNCIL;
    return (
      <button
        key={p.id}
        type="button"
        onClick={() => toggle(p.id)}
        disabled={disabled || atLimit}
        data-testid={`council-persona-${p.id}`}
        aria-pressed={isOn}
        title={p.specialty || p.display_name}
        className={`flex items-center gap-1.5 px-2.5 h-8 rounded-full border text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          isOn
            ? "border-primary/30 bg-primary/10 text-primary"
            : "border-border bg-transparent text-muted-foreground hover:text-foreground hover:border-primary/30"
        }`}
      >
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: p.color || "#94a3b8" }}
        />
        <span className="font-medium">{p.display_name}</span>
        {isOn && <Check className="w-3 h-3 flex-shrink-0" />}
      </button>
    );
  };

  return (
    <div className="rounded-md border border-border bg-card/50 p-3 md:p-4" data-testid="council-selector">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Users className="w-4 h-4 text-primary flex-shrink-0" />
        <p className="text-xs font-medium text-foreground">
          {t("query.councilForThisQuery")}
        </p>
        <span className="text-xs text-muted-foreground">
          {t("query.specialistsSelected").replace("{count}", String(selected.length))}
        </span>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => !disabled && setSelectedCouncil([])}
            disabled={disabled}
            data-testid="council-clear-automatic"
            className="text-xs text-primary hover:underline disabled:opacity-50 ml-auto"
          >
            {t("query.councilClearAutomatic")}
          </button>
        )}
      </div>

      {selected.length === 0 && (
        <p className="text-[11px] text-muted-foreground" data-testid="council-empty-hint">
          {t("query.councilAutomatic")}
        </p>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {personas.filter((p) => selected.includes(p.id)).map(renderChip)}
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        data-testid="council-expand"
        aria-expanded={expanded}
        className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? t("query.councilCollapseSpecialists") : t("query.councilExpandSpecialists")}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("query.councilSearchPlaceholder")}
              data-testid="council-search"
              className="w-full h-8 pl-8 pr-2 rounded-md border border-border bg-background text-xs"
            />
          </div>
          {grouped.map((bucket) => (
            <div key={bucket.group}>
              <p className="text-[11px] font-medium text-muted-foreground mb-1.5">
                {groupLabel[bucket.group] || bucket.group}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {bucket.items.map(renderChip)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CouncilSelector;
